import {
  ApiError,
  FileDropzoneRejectReason,
  sendNotification,
  useApiClient,
  useFormMetadata,
  useTranslation,
} from '@msdk/common'
import {
  AttachmentValue,
  Entity,
  EntityModel,
  FormModel,
  GenericFieldValue,
  LookupValue,
  readEntityModelByName,
  readUserData,
} from '@msdk/definitions'
import { stringFormat } from '@msdk/forms'
import { Box, CheckIcon, Spinner, styled } from '@msdk/ui'
import { useEffect, useState } from 'react'
import { useWizard } from 'react-use-wizard'

import { FooterPopup } from './FooterPopup'
import { HeaderPopup } from './HeaderPopup'
import { PhotoDragAndDrop } from './PhotoDragAndDrop'
import { PhotoGuidlines } from './PhotoGuidlines'
import {
  ModalStepProps,
  StyledContainer,
  StyledEditContent,
  StyledList,
  StyledListElement,
  StyledListItem,
  StyledMessageTypography,
} from './shared'

const ACCEPTED_FILE_TYPES = '.jpg,.png'
const MAX_FILE_SIZE = 5 * 1024 * 1024
const MIN_IMAGE_SIZE = 640

type ImageDimensions = {
  width: number
  height: number
}

const StyledSpinnerContainer = styled(Box)`
  width: 100%;
  height: 100%;
  position: absolute;
  align-content: center;
  z-index: 1;
  text-align: center;
`

const StyledSpinner = styled(Spinner)`
  width: 100px !important;
  height: 100px !important;
`

async function readImageDimensions(file: File): Promise<ImageDimensions> {
  return new Promise((resolve, reject) => {
    const objectUrl = window.URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => {
      window.URL.revokeObjectURL(objectUrl)
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
      })
    }

    image.onerror = () => {
      window.URL.revokeObjectURL(objectUrl)
      reject(new Error(`Unable to read image dimensions for ${file.name}`))
    }

    image.src = objectUrl
  })
}

type UploadPhotoPopupProps = ModalStepProps & {
  uploadedPhoto: Entity | undefined
}

export function UploadPhotoPopup({ uploadedPhoto, ...props }: UploadPhotoPopupProps) {
  const { goToStep } = useWizard()
  const t = useTranslation()
  const apiClient = useApiClient()
  const formModel = useFormMetadata('USER_FACE_IMAGE', 'USER_FACE_IMAGE_FORM') as FormModel
  const [isLoading, setIsLoading] = useState(false)
  const [isUploadedCompleted, setIsUploadedCompleted] = useState(true)
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined)
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions | undefined>(undefined)
  const acceptedExtensions = ACCEPTED_FILE_TYPES.split(',').map((value) => value.trim().toLowerCase())
  const fileExtension = selectedFile?.name.includes('.')
    ? `.${selectedFile.name.split('.').pop()?.toLowerCase() ?? ''}`
    : ''
  const isValidType = !!selectedFile && acceptedExtensions.includes(fileExtension)
  const isValidSize = !!selectedFile && selectedFile.size <= MAX_FILE_SIZE
  const isValidDimensions =
    !!imageDimensions && imageDimensions.width >= MIN_IMAGE_SIZE && imageDimensions.height >= MIN_IMAGE_SIZE

  const requirements: { id: string; label: string; test: () => boolean }[] = [
    {
      id: 'type',
      label: t('LBL_NGID_UPLOAD_PHOTO_RULE1', 'JPG or PNG'),
      test: () => isValidType,
    },
    {
      id: 'size',
      label: t('LBL_NGID_UPLOAD_PHOTO_RULE2', 'Up to 5 MB'),
      test: () => isValidSize,
    },
    {
      id: 'dimensions',
      label: t('LBL_NGID_UPLOAD_PHOTO_RULE4', 'At least 640 x 640 px'),
      test: () => isValidDimensions,
    },
  ]

  function handleClear() {
    setSelectedFile(undefined)
    setImageDimensions(undefined)
  }

  function notifyImageReadError() {
    handleClear()
    sendNotification({
      message: t('LBL_NGID_UPLOAD_PHOTO_ERROR_MSG', 'Cannot read the selected image. Please try another file.'),
      status: 'error',
      title: t('LBL_NGID_UPLOAD_PHOTO_ERROR_TITLE', 'Upload image error'),
    })
  }

  const onFieldChange = async (targetFile?: File) => {
    if (!targetFile) {
      return
    }
    try {
      const dimensions = await readImageDimensions(targetFile)
      setSelectedFile(targetFile)
      setImageDimensions(dimensions)
    } catch {
      notifyImageReadError()
    }
  }

  const errorMessageMap = {
    [FileDropzoneRejectReason.InvalidSize]: t(
      'LBL_ATTACHMENT_TOO_BIG',
      'The file {0} exceeds the maximum allowed size',
    ),
    [FileDropzoneRejectReason.InvalidType]: t(
      'LBL_ATTACHMENT_INVALID_EXTENSION',
      'The file {0} has an invalid extension or type!',
    ),
  }

  const handleFileReject = (rejectedFile: File, reason: FileDropzoneRejectReason) => {
    sendNotification({
      message: stringFormat(errorMessageMap[reason], rejectedFile.name),
      status: 'error',
      title: t('LBL_UPLOAD_PHOTO_FILE_REJECTED', 'The file was rejected'),
    })
  }

  const createEntityToSave = async (entityModel: EntityModel): Promise<Entity> => {
    const fieldValues: Record<string, GenericFieldValue> = {}
    const loggedUserLookup = new LookupValue(readUserData().user_id)
    loggedUserLookup.entityName = 'USER'
    fieldValues.USER = loggedUserLookup
    const createResult = await apiClient.entity.createEntity(
      'USM',
      entityModel,
      undefined,
      false,
      undefined,
      fieldValues,
    )
    const entity = createResult.entities[0]
    entity.fields.FACE_IMAGE = new AttachmentValue(selectedFile!, selectedFile!.name)
    return entity
  }

  const handlePhotoSave = async () => {
    if (selectedFile) {
      setIsLoading(true)
      const entityModel = readEntityModelByName('USER_FACE_IMAGE') as EntityModel
      const entityToSave = await createEntityToSave(entityModel)
      const res = await apiClient.entity.saveEntities('USM', entityModel, [entityToSave], formModel)
      setIsLoading(false)
      if (res instanceof ApiError) {
        console.error('[UploadPhotoPopup] - Error saving photo', res)
        sendNotification({
          message: t('LBL_NGID_UPLOAD_PHOTO_ERROR_SAVING', 'Error saving photo'),
          status: 'error',
        })
        setIsUploadedCompleted(false)
      } else {
        sendNotification({
          message: t(
            'LBL_NGID_FACE_RECO_PHOTO_NOTIFIC_MSG',
            'You can now log in by looking at the camera on the dialysis machine.',
          ),
          status: 'success',
          title: t('LBL_NGID_FACE_RECO_PHOTO_NOTIFIC_TITLE', 'Face recognition photo uploaded'),
        })
        setIsUploadedCompleted(true)
        goToStep(3)
      }
    }
  }

  useEffect(() => {
    const requirementCheck = requirements.every((requirement) => requirement.test())
    setIsUploadedCompleted(requirementCheck)
  }, [selectedFile])

  return (
    <>
      {isLoading && (
        <StyledSpinnerContainer>
          <StyledSpinner color="info" />
        </StyledSpinnerContainer>
      )}
      <HeaderPopup title={t('LBL_NGID_FACE_RECO_TITLE', 'Face recognition')} modalRef={props.modalRef} />
      <StyledEditContent>
        <StyledMessageTypography variant="body1">
          {t(
            'LBL_NGID_FACE_RECO_LONG_DESCR',
            'Setup face recognition by uploading your photo. Use a clear, recent photo of your face.',
          )}
        </StyledMessageTypography>
        {!selectedFile && (
          <PhotoDragAndDrop
            accept={ACCEPTED_FILE_TYPES}
            maxSize={MAX_FILE_SIZE}
            onFiles={onFieldChange}
            onFilesReject={handleFileReject}
            onClear={handleClear}
          />
        )}
        {selectedFile && (
          <PhotoDragAndDrop
            accept={ACCEPTED_FILE_TYPES}
            maxSize={MAX_FILE_SIZE}
            onFiles={onFieldChange}
            onFilesReject={handleFileReject}
            selectedFile={selectedFile}
            onClear={handleClear}
            isUploadCompleted={isUploadedCompleted}
          />
        )}
        <StyledContainer>
          <StyledListItem>{t('LBL_NGID_FACE_RECO_REQUIREMENTS', 'Image requirements:')}</StyledListItem>
          <StyledList>
            {requirements.map((r) => {
              const satisfied = r.test()
              const notSatisfied = !!selectedFile && !satisfied
              return (
                <StyledListElement key={r.id} $satisfied={satisfied} $notSatisfied={notSatisfied}>
                  {satisfied && (
                    <CheckIcon aria-hidden fontSize="small" style={{ position: 'absolute', left: '-1.25em' }} />
                  )}
                  {r.label}
                </StyledListElement>
              )
            })}
          </StyledList>
        </StyledContainer>
        <PhotoGuidlines />
      </StyledEditContent>
      <FooterPopup
        secondary={{
          label: t('LBL_NGID_BACK_BTN', 'Back'),
          onClick: () => goToStep(5),
        }}
        primary={{
          label: t('LBL_NGID_SAVE_BTN', 'Save'),
          onClick: handlePhotoSave,
          disabled: !isUploadedCompleted || isLoading,
        }}
      />
    </>
  )
}
